import { AppConstants } from 'src/app/app-constants';
import { parseRelativeDateToUtc } from 'src/app/utils/date-parser.util'
import allUsersAllSessionsCount from 'src/assets/demo-data/reportingDashboard/allUsersAllSessionsCount.json';
import simulationMetrics from 'src/assets/demo-data/reportingDashboard/simulationMetrics.json';

export class DemoModeHelper {

  
  // Deterministically maps a userUuid to a set index (0..setCount-1) via polynomial rolling hash.
  // Same UUID always returns the same index, distributing evenly across sets.
  private static getDemoSetIndex(userUuid: string, setCount: number): number {
    let hash = 0;
    for (let i = 0; i < userUuid.length; i++) {
      hash = (hash * 31 + userUuid.charCodeAt(i)) >>> 0;
    }
    return hash % setCount;
  }

  static filterDemoDataByRequest(data: any[], body: any, matchedKey?: string): any[] {
    if (!data?.length) return [];
    if (!body || Object.keys(body).length === 0) return data;

    if (matchedKey === 'individualAssignmentView/sessionStats' && body.simulationUuid) {
      return this.injectSimulationInfoIntoSessions(data, body.simulationUuid);
    }

    if (matchedKey === 'usersView/getUserSessionsSummary') {
      const matchingSets = body.accountUuid
        ? data.filter((set: any) => set.accountUuid === body.accountUuid)
        : data;
      if (!matchingSets.length) return [];
      const setIndex = this.getDemoSetIndex(body.userUuid ?? '', matchingSets.length);
      return matchingSets[setIndex].cards;
    }

    const filtered = data.filter(item => {
        return Object.entries(body).every(([key, value]) => {
           
            if ( matchedKey === 'usersView/getUserAssignmentMetrics' && key === 'moduleAssignmentUuids') {
              return this.matchModuleAssignmentUuids(item, value as string[] | null | undefined, 'assignmentUuid', true);
            }

            if ( matchedKey === 'usersView/getIndividualUserSessions' && key === 'moduleAssignmentUuids') {
              return this.matchModuleAssignmentUuids(item, value as string[] | null, 'moduleAssignmentUuid' );
            }

            if (matchedKey === 'usersView/getUserAssignments' && key === 'moduleAssignmentUuids') {
              return this.matchModuleAssignmentUuids(item, value as string[] | null, 'assignmentUuid');
            }

            if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
              return true; 
            }

            if (!(key in item)) {
              return true;
            }

            if (key === 'teamUuids') {
              const uuids = value as string[];
              return Array.isArray(item.teamUuids) && item.teamUuids.some((uuid: string) => uuids.includes(uuid));
            }

            const eqMatch = item[key] === value;
            return eqMatch;
        });
    });

    if (matchedKey === 'usersView/getIndividualUserSessions' && body.type === AppConstants.TYPE_PRACTICE) {
      return this.limitSessionsPerUserAndAssignment(
        filtered,
        body.moduleAssignmentUuids ?? null,
        body.userUuid ?? null
      );
    }

    return filtered;
  }

  // Returns sessions for a user, limited to the hardcoded expected count per assignment.
  private static limitSessionsPerUserAndAssignment(
    sessions: any[],
    moduleAssignmentUuids: string[] | null,
    userUuid: string | null
  ): any[] {
    if (!userUuid) return sessions;

    const grouped = new Map<string, any[]>();
    for (const session of sessions) {
      const assignmentUuid = session.moduleAssignmentUuid;

      if (moduleAssignmentUuids?.length && !moduleAssignmentUuids.includes(assignmentUuid)) continue;
 
      if (!grouped.has(assignmentUuid)) grouped.set(assignmentUuid, []);
      grouped.get(assignmentUuid)!.push(session);
    }

    const result: any[] = [];

    for (const [assignmentUuid, assignmentSessions] of grouped.entries()) {
      const expectedCount = this.getExpectedSessionCount(userUuid, assignmentUuid);

      if (expectedCount != null) {
        result.push(...assignmentSessions.slice(0, expectedCount));
      } else {
        result.push(...assignmentSessions);
      }
    }

    return result;
  }

  private static getExpectedSessionCount(
  userUuid: string,
  moduleAssignmentUuid: string
  ): number | null {
    const userEntry = allUsersAllSessionsCount.find(
      entry => entry.userUuid === userUuid
    );

    if (!userEntry) {
      return null;
    }

    const count = (userEntry as any)[moduleAssignmentUuid];
    const result = typeof count === 'number' ? count : null;
    return result;
  }

  // Session demo data uses 'SIMULATION_VIEW' as a placeholder moduleAssignmentUuid so the same
  // records work across any assignment. At request time we filter to those rows and stamp the
  // real simulationUuid + simulationName (looked up from simulationMetrics) onto each one.
  private static injectSimulationInfoIntoSessions(data: any[], simulationUuid: string): any[] {
    const simName = (simulationMetrics as any[]).find(s => s.uuid === simulationUuid)?.simulationName ?? '';
    return data
      .filter((item: any) => item.moduleAssignmentUuid === 'SIMULATION_VIEW')
      .map((item: any) => ({ ...item, simulationUuid, simulationName: simName }));
  }

  static matchModuleAssignmentUuids(
    item: any,
    moduleAssignmentUuids: string[] | null | undefined,
    itemKey: string,
    aggregatedMode = false
  ): boolean {
    if (!moduleAssignmentUuids || moduleAssignmentUuids.length === 0) {
      return aggregatedMode ? item[itemKey] == null : true;
    }

    return moduleAssignmentUuids.includes(item[itemKey]);
  } 

  static overrideAccountUuid(body: any, demoUuid = '123'): any {
    if (body && typeof body === 'object' && 'accountUuid' in body) {
      return { ...body, accountUuid: demoUuid };
    }
    return body;
  }

  static normalizeModuleAssignmentStats(data: any[]): any {
    const item = data[0];
    return {
      ...item,
      startDate: parseRelativeDateToUtc(item.startDate),
      endDate: parseRelativeDateToUtc(item.endDate),
    };
  }

  private static assignmentStatsNormalize(data: any[]): any[] {
      return data.map(item => ({
          ...item,
          startDate: parseRelativeDateToUtc(item.startDate),
          endDate: parseRelativeDateToUtc(item.endDate)
      }));
  }

  static normalizeDemoData(mockData: any[], matchedKey?: string): any {
    if (!mockData?.length) mockData = [];

    if (matchedKey === '/findLeaderboardAggregates') {
        const obj = mockData[0] || { totalEngagedUsersCount: 0, leaderboardAggregates: [] };

        obj.leaderboardAggregates?.forEach((row: any) => {
            row.lastPractice = parseRelativeDateToUtc(row.lastPractice);
        });

        return obj;
    }

     if (matchedKey === '/certificationAssignmentStats' || matchedKey === '/practiceAssignmentStats') {
        return DemoModeHelper.assignmentStatsNormalize(mockData);
    }

     if (matchedKey === 'individualAssignmentView/engagementStatus') return mockData[0] ?? {};

     if (matchedKey === 'individualSimulationView/simulationStats') return mockData[0];

     if (matchedKey === 'individualAssignmentView/moduleAssignmentStats') {
        return DemoModeHelper.normalizeModuleAssignmentStats(mockData);
     }


     if (matchedKey === 'individualAssignmentView/certificationAssignmentUserStats') {
        return DemoModeHelper.parseAssignmentUserStatsDates(mockData);
    }

    if (matchedKey === 'individualAssignmentView/sessionStats') {
        return DemoModeHelper.parseAssignmentSessionsStatsDates(mockData);
    }

    if (matchedKey === 'usersView/getUsersData') {
        return DemoModeHelper.parseUserLastPracticeDates(mockData);
    }

    if (matchedKey === 'usersView/getUserSummary') {
        return DemoModeHelper.normalizeUserSummary(mockData);
    }

    if (matchedKey === 'usersView/getIndividualUserSessions') {
        return DemoModeHelper.parseSessionScoredAt(mockData);
    }

    if (matchedKey === 'usersView/getUserAssignmentMetrics') return mockData[0];

    if (matchedKey === 'usersView/getUserAssignments') {
      return DemoModeHelper.normalizeUserAssignments(mockData);
    }

    return mockData;
   }

  static normalizeUserAssignments(data: any[]): any[] {
    const dateFields = ['lastAttemptDate', 'dateCertification'];

    return data.map(row => {
      const normalizedRow = { ...row };

      dateFields.forEach(field => {
        if (normalizedRow[field] !== null && normalizedRow[field] !== undefined) {
          normalizedRow[field] = parseRelativeDateToUtc(normalizedRow[field]);
        }
      });

      return normalizedRow;
    });
  }

  static normalizeUserSummary(data: any[]): any {
    const row = data[0];
    return {
      ...row,
      lastLoginDate: row.lastLoginDate !== null
        ? parseRelativeDateToUtc(row.lastLoginDate)
        : null
    };
  }

  static parseSessionScoredAt(data: any[]): any[] {
    return data.map(row => ({
        ...row,
        scoredAt: row.scoredAt !== null
            ? parseRelativeDateToUtc(row.scoredAt)
            : null
    }));
  }

  static parseUserLastPracticeDates(data: any[]): any[] {
    return data.map(row => ({
        ...row,
        lastPracticeDate: row.lastPracticeDate !== null
            ? parseRelativeDateToUtc(row.lastPracticeDate)
            : null
    }));
  }

  static parseAssignmentUserStatsDates(data: any[]): any[] {
    return data.map(row => ({
        ...row,
        dateOfCertification: row.dateOfCertification !== 'N/A'
            ? parseRelativeDateToUtc(row.dateOfCertification, 'datetime')
            : 'N/A'
    }));
  }

  static parseAssignmentSessionsStatsDates(data: any[]): any[] {
    return data.map(row => ({
        ...row,
        sessionDate: row.sessionDate !== 'N/A'
            ? parseRelativeDateToUtc(row.sessionDate, 'long')
            : 'N/A'
    }));
  }

   static getQueryParamsFromUrl(url: string): Record<string, string> {
      const query: Record<string, string> = {};
      const queryString = url.split('?')[1];
      if (!queryString) return query;

      queryString.split('&').forEach(pair => {
        const [key, value] = pair.split('=');
        if (key) query[key] = decodeURIComponent(value || '');
      });

      return query;
    }

  static removeAccountUuidFromRequestBody(
    requestData: Record<string, any> | null
  ): Record<string, any> | null {
    if (!requestData) {
      return requestData;
    }

    const { accountUuid, ...rest } = requestData;
    return Object.keys(rest).length ? rest : null;
  }

}
